/** The normalized shape both send sites read, whichever channel answered. */
export type SendCodeResult = {
  verificationId?: string;
  success?: boolean;
  whatsappUnavailable?: boolean;
};

/**
 * Did a send actually deliver a code?
 *
 * The endpoint reports a refused WhatsApp send as **HTTP 200** with
 * `{ success: false, whatsappUnavailable: true }`,
 * so a caller that only checks for a thrown error, or only for a `verificationId`, will
 * tell the user a code is on its way when none was sent. The two channels also succeed
 * differently: SMS returns a `verificationId` the code step verifies against, while
 * WhatsApp returns none because the recipient service checks the code itself.
 *
 * Kept as a pure function so both the phone step and the code step's resend answer this
 * the same way — they diverged once, and only the phone step was right.
 */
export function wasCodeSent(data: SendCodeResult, isWhatsApp: boolean): boolean {
  if (isWhatsApp) return data.success === true && data.whatsappUnavailable !== true;
  return data.success === true && !!data.verificationId;
}

/**
 * The RAW shape the serverless endpoint answers with, which the browser now sees directly.
 *
 * It is not the shape above: SMS reports success nested under `result`, WhatsApp reports it at the top
 * level and carries no `verificationId` (the recipient service checks that code itself). An SMS
 * `verificationId` is an int64 above 2^53, so it travels as a decimal STRING the whole way: a number would
 * already have been rounded to a different id by `JSON.parse`. An API route used to iron all of that out
 * server-side; the browser calls the endpoint itself now, so it is done here instead — in ONE place,
 * because the two send sites diverged once before and only one of them was right.
 */
interface RawSendCodeResponse {
  result?: { success?: boolean };
  success?: boolean;
  whatsappUnavailable?: boolean;
  verificationId?: string;
}

export function normalizeSendCodeResponse(raw: unknown): SendCodeResult {
  const data = (raw ?? {}) as RawSendCodeResponse;
  return {
    success: data.result?.success === true || data.success === true,
    verificationId: data.verificationId === undefined ? undefined : String(data.verificationId),
    ...(data.whatsappUnavailable ? { whatsappUnavailable: true } : {}),
  };
}
